import type {
  ACLConfig,
  ACLEngine,
  CacheEntry,
  EvaluationContext,
  EvaluationResult,
  PatternMatcher,
  Permission,
  PermissionAction,
  PermissionRule,
  ResourceType,
  Role,
  RoleName,
  User,
} from "./types";

/**
 * Motor ACL inteligente com suporte a wildcards, cache e herança
 */
export class ACL<TContext = any> implements ACLEngine<TContext> {
  private roles: Map<RoleName, Role<TContext>> = new Map();
  private cache: Map<string, CacheEntry> = new Map();
  private config: Required<ACLConfig>;
  private patternMatcher: PatternMatcher;

  constructor(config: ACLConfig = {}) {
    this.config = {
      wildcardPattern: "*",
      caseSensitive: false,
      cache: true,
      cacheMaxSize: 1000,
      cacheTTL: 5 * 60 * 1000, // 5 minutos
      defaultDeny: true,
      debug: false,
      ...config,
    };

    this.patternMatcher = this.createPatternMatcher();
  }

  /**
   * Cria um matcher de patterns com suporte a wildcards
   */
  private createPatternMatcher(): PatternMatcher {
    const { wildcardPattern, caseSensitive } = this.config;

    return (pattern: string, value: string): boolean => {
      // Normaliza strings se não for case sensitive
      if (!caseSensitive) {
        pattern = pattern.toLowerCase();
        value = value.toLowerCase();
      }

      // Wildcard completo
      if (pattern === wildcardPattern) return true;

      // Converte pattern para regex
      const regexPattern = pattern
        .split(wildcardPattern)
        .map((part) => part.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
        .join(".*");

      const regex = new RegExp(`^${regexPattern}$`);
      return regex.test(value);
    };
  }

  /**
   * Define uma role no sistema
   */
  defineRole(role: Role<TContext>): void {
    this.roles.set(role.name, role);
    this.clearCache(); // Limpa cache ao modificar roles
  }

  /**
   * Remove uma role do sistema
   */
  removeRole(name: RoleName): void {
    this.roles.delete(name);
    this.clearCache();
  }

  /**
   * Obtém uma role pelo nome
   */
  getRole(name: RoleName): Role<TContext> | undefined {
    return this.roles.get(name);
  }

  /**
   * Verifica se usuário tem permissão
   */
  can(
    user: User<TContext>,
    action: PermissionAction,
    resource?: ResourceType,
    context?: TContext,
  ): boolean {
    const result = this.evaluate(user, action, resource, context);
    return result.allowed;
  }

  /**
   * Avalia permissão com detalhes
   */
  evaluate(
    user: User<TContext>,
    action: PermissionAction,
    resource?: ResourceType,
    context?: TContext,
  ): EvaluationResult {
    // Verifica cache
    if (this.config.cache) {
      const cached = this.checkCache(user, action, resource, context);
      if (cached) return cached;
    }

    // Cria contexto de avaliação
    const evalContext: EvaluationContext = {
      user,
      resource: (context as any)?.resource || resource,
      metadata: { action, resourceType: resource },
    };

    // Coleta todas as permissões aplicáveis
    const permissions = this.getUserPermissions(user);

    // Ordena por prioridade (maior primeiro) e especificidade
    const sortedPermissions = this.sortPermissions(permissions);

    // Avalia cada permissão
    for (const permission of sortedPermissions) {
      const matches = this.matchesPermission(
        permission,
        action,
        evalContext,
        context,
        resource,
      );

      if (matches) {
        const result: EvaluationResult = {
          allowed: !permission.deny,
          reason: permission.deny
            ? `Explicitly denied by rule: ${this.formatRule(permission)}`
            : `Allowed by rule: ${this.formatRule(permission)}`,
          matchedRule: permission,
        };

        // Salva no cache
        if (this.config.cache) {
          this.saveToCache(user, action, resource, result, context);
        }

        return result;
      }
    }

    // Sem match - usa default
    const result: EvaluationResult = {
      allowed: !this.config.defaultDeny,
      reason: this.config.defaultDeny
        ? "No matching permission found (default deny)"
        : "No matching permission found (default allow)",
    };

    if (this.config.cache) {
      this.saveToCache(user, action, resource, result, context);
    }

    return result;
  }

  /**
   * Obtém todas as permissões de um usuário (incluindo roles)
   */
  getUserPermissions(user: User<TContext>): PermissionRule<TContext>[] {
    const permissions: PermissionRule<TContext>[] = [];

    // Permissões diretas do usuário
    if (user.permissions) {
      permissions.push(...user.permissions);
    }

    // Permissões das roles (com herança)
    const processedRoles = new Set<RoleName>();

    for (const roleName of user.roles) {
      this.collectRolePermissions(roleName, permissions, processedRoles);
    }

    return permissions;
  }

  /**
   * Coleta permissões de uma role recursivamente (herança)
   */
  private collectRolePermissions(
    roleName: RoleName,
    permissions: PermissionRule<TContext>[],
    processedRoles: Set<RoleName>,
  ): void {
    if (processedRoles.has(roleName)) return;
    processedRoles.add(roleName);

    const role = this.roles.get(roleName);
    if (!role) return;

    // Adiciona permissões da role
    permissions.push(...role.permissions);

    // Processa roles herdadas
    if (role.inherits) {
      for (const inheritedRole of role.inherits) {
        this.collectRolePermissions(inheritedRole, permissions, processedRoles);
      }
    }
  }

  /**
   * Obtém hierarquia completa de uma role
   */
  getRoleHierarchy(roleName: RoleName): RoleName[] {
    const hierarchy: RoleName[] = [];
    const processedRoles = new Set<RoleName>();

    const collectHierarchy = (name: RoleName) => {
      if (processedRoles.has(name)) return;
      processedRoles.add(name);

      const role = this.roles.get(name);
      if (!role) return;

      hierarchy.push(name);

      if (role.inherits) {
        for (const inheritedRole of role.inherits) {
          collectHierarchy(inheritedRole);
        }
      }
    };

    collectHierarchy(roleName);
    return hierarchy;
  }

  /**
   * Verifica se uma permissão corresponde à ação/recurso
   */
  private matchesPermission(
    permission: PermissionRule<TContext>,
    action: PermissionAction,
    evalContext: EvaluationContext,
    context?: TContext,
    resource?: ResourceType,
  ): boolean {
    // Verifica ação
    const actions = Array.isArray(permission.action)
      ? permission.action
      : [permission.action];

    const actionMatches = actions.some((a) => this.patternMatcher(a, action));

    if (!actionMatches) return false;

    // Verifica recurso (se especificado)
    if (permission.resource) {
      if (!resource) return false;

      const resources = Array.isArray(permission.resource)
        ? permission.resource
        : [permission.resource];

      const resourceMatches = resources.some((r) =>
        this.patternMatcher(r, resource),
      );

      if (!resourceMatches) return false;
    }

    // Verifica condições
    if (permission.conditions && permission.conditions.length > 0) {
      // Se context customizado foi passado, usa ele
      // Senão, constrói contexto com user e resource do evalContext
      const conditionContext =
        context !== undefined
          ? context
          : ({
              user: evalContext.user,
              resource: evalContext.resource,
              metadata: evalContext.metadata,
            } as TContext);

      const allConditionsMet = permission.conditions.every((condition) =>
        condition(conditionContext),
      );

      if (!allConditionsMet) return false;
    }

    return true;
  }

  /**
   * Ordena permissões por prioridade e especificidade
   */
  private sortPermissions(
    permissions: PermissionRule<TContext>[],
  ): PermissionRule<TContext>[] {
    return permissions.sort((a, b) => {
      // Prioridade explícita
      if (a.priority !== undefined && b.priority !== undefined) {
        return b.priority - a.priority;
      }
      if (a.priority !== undefined) return -1;
      if (b.priority !== undefined) return 1;

      // Deny tem precedência sobre allow
      if (a.deny && !b.deny) return -1;
      if (!a.deny && b.deny) return 1;

      // Regras com condições têm precedência sobre regras sem condições
      const aHasConditions = a.conditions && a.conditions.length > 0;
      const bHasConditions = b.conditions && b.conditions.length > 0;

      if (aHasConditions && !bHasConditions) return -1;
      if (!aHasConditions && bHasConditions) return 1;

      // Mais específico primeiro (menos wildcards)
      const aSpecificity = this.calculateSpecificity(a);
      const bSpecificity = this.calculateSpecificity(b);

      return bSpecificity - aSpecificity;
    });
  }

  /**
   * Calcula especificidade de uma regra (mais alto = mais específico)
   */
  private calculateSpecificity(rule: PermissionRule<TContext>): number {
    let score = 0;

    // Ações específicas valem mais que wildcards
    const actions = Array.isArray(rule.action) ? rule.action : [rule.action];
    for (const action of actions) {
      if (!action.includes(this.config.wildcardPattern)) {
        score += 10;
      }
    }

    // Recursos específicos valem mais
    if (rule.resource) {
      const resources = Array.isArray(rule.resource)
        ? rule.resource
        : [rule.resource];
      for (const resource of resources) {
        if (!resource.includes(this.config.wildcardPattern)) {
          score += 10;
        }
      }
    }

    // Condições adicionam MUITA especificidade (devem ter prioridade)
    if (rule.conditions && rule.conditions.length > 0) {
      score += rule.conditions.length * 20;
    }

    return score;
  }

  /**
   * Formata regra para mensagem
   */
  private formatRule(rule: PermissionRule<TContext>): string {
    const parts = [];

    const actions = Array.isArray(rule.action) ? rule.action : [rule.action];
    parts.push(`action=${actions.join(",")}`);

    if (rule.resource) {
      const resources = Array.isArray(rule.resource)
        ? rule.resource
        : [rule.resource];
      parts.push(`resource=${resources.join(",")}`);
    }

    if (rule.conditions) {
      parts.push(`conditions=${rule.conditions.length}`);
    }

    return parts.join(", ");
  }

  /**
   * Verifica cache
   */
  private checkCache(
    user: User<TContext>,
    action: PermissionAction,
    resource?: ResourceType,
    context?: TContext,
  ): EvaluationResult | null {
    const key = this.getCacheKey(user, action, resource, context);
    const entry = this.cache.get(key);

    if (!entry) return null;

    // Verifica TTL
    if (Date.now() - entry.timestamp > this.config.cacheTTL) {
      this.cache.delete(key);
      return null;
    }

    if (this.config.debug) {
      console.log(`[ACL] Cache hit: ${key}`);
    }

    return entry.result;
  }

  /**
   * Salva no cache
   */
  private saveToCache(
    user: User<TContext>,
    action: PermissionAction,
    resource: ResourceType | undefined,
    result: EvaluationResult,
    context?: TContext,
  ): void {
    // Limita tamanho do cache
    if (this.cache.size >= this.config.cacheMaxSize) {
      // Remove entradas mais antigas
      const entriesToDelete = Math.floor(this.config.cacheMaxSize * 0.2); // Remove 20%
      const sortedEntries = Array.from(this.cache.entries()).sort(
        (a, b) => a[1].timestamp - b[1].timestamp,
      );

      for (let i = 0; i < entriesToDelete; i++) {
        this.cache.delete(sortedEntries[i][0]);
      }
    }

    const key = this.getCacheKey(user, action, resource, context);
    this.cache.set(key, {
      key,
      result,
      timestamp: Date.now(),
    });
  }

  /**
   * Gera chave de cache
   */
  private getCacheKey(
    user: User<TContext>,
    action: PermissionAction,
    resource?: ResourceType,
    context?: TContext,
  ): string {
    const parts = [
      `user:${user.id}`,
      `action:${action}`,
      `resource:${resource || "none"}`,
      `roles:${user.roles.join(",")}`,
    ];

    // Inclui hash do contexto se fornecido
    if (context) {
      const contextHash = this.hashContext(context);
      parts.push(`context:${contextHash}`);
    }

    return parts.join("|");
  }

  /**
   * Gera hash simples do contexto para cache
   */
  private hashContext(context: TContext): string {
    try {
      return JSON.stringify(context);
    } catch {
      return "unparseable";
    }
  }

  /**
   * Limpa todo o cache
   */
  clearCache(): void {
    this.cache.clear();
  }
}

/**
 * Factory function para criar instância ACL
 */
export function createACL<TContext = any>(config?: ACLConfig): ACL<TContext> {
  return new ACL<TContext>(config);
}
